"use client";
import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { fetchUser, getAuthHeaders } from "@/app/funcs/api";
import { canEditArticle } from "@/app/funcs/articleEditPermissions";
import ArticleBodyEditor from "./ArticleBodyEditor";
import { deleteDraft, listDrafts, upsertDraft } from "@/app/utils/draftsDb";

/**
 * ACF field definitions per UI type (keys must match ACF field "Name").
 * We will POST them inside an `acf` object so ACF saves them directly.
 */
const ACF_FIELDS = {
  posts: {
    excerpt: { type: "textarea", label: "ملخص المقال", required: false },
    source_link: { type: "text", label: "رابط المصدر ( في حال المقال مترجم )", required: false },
    category: { type: "select", label: "الفئة", required: false, options: ["تطوير الويب", "تصميم", "أدوات", "تعليمي", "أخبار"] },
  },
  figplg: {
    link: { type: "url", label: "رابط الإضافة", required: true },
  },
  figtemp: {
    link: { type: "url", label: "رابط القالب", required: true },
    temp_type: { type: "select", label: "نوع القالب", required: false, options: ["UI Kit", "Dashboard", "Landing Page", "Mobile App", "Slides"] },
  },
  project: {
    project_url: { type: "url", label: "رابط المشروع", required: false },
  },
  job: {
    company: { type: "text", label: "اسم الشركة", required: true },
    country: { type: "text", label: "الموقع", required: false },
    city: { type: "text", label: "المدينة", required: false },
    salary_range: { type: "text", label: "نطاق الراتب", required: false },
    job_type: { type: "select", label: "نوع الوظيفة", required: false, options: ["دوام كامل", "دوام جزئي", "عن بُعد", "عقد مؤقت"] },
    experience_level: { type: "select", label: "مستوى الخبرة", required: false, options: ["مبتدئ", "متوسط", "متقدم", "خبير"] },
    application_deadline: { type: "date", label: "آخر موعد للتقديم", required: false },
    hiring_email: { type: "email", label: "إيميل استقبال التقديمات", required: false },
    hiring_link: { type: "url", label: "رابط التقديم ", required: false },
  },
  challenge: {
    challenge_type: { type: "select", label: "نوع التحدي", required: true, options: ["تصميم", "تطوير", "UX/UI", "إبداعي"] },
    difficulty: { type: "select", label: "مستوى الصعوبة", required: true, options: ["سهل", "متوسط", "صعب", "خبير"] },
    deadline: { type: "date", label: "موعد انتهاء التحدي", required: true },
    users: { type: "users", label: "المشاركون", required: false },
  },
  designreview: {
    figma_link: { type: "url", label: "رابط مشروع Figma", required: true },
    extra_link: { type: "url", label: "رابط إضافي (Figma Jam أو غيره)", required: false },
    agreement: { type: "select", label: "النشر على الموقع فيجماوية والسماح للأعضاء بالمراجعة والتعليق ؟", required: true, options: ["نعم", "لا"] },
  },
  podcast: {
    session: { type: "text", label: "الموسم", required: false },
    program: { type: "text", label: "البرنامج", required: false },
    episode_number: { type: "number", label: "رقم الحلقة", required: false },
    author: { type: "text", label: "اسم الكاتب", required: false },
    podcaster: { type: "text", label: "اسم المقدم (رقم العضوية في فيجماوية)", required: false },
    duration: { type: "text", label: "مدة الحلقة", required: false },
    guest_name: { type: "text", label: "اسم الضيف", required: false },
    tags: { type: "text", label: "الوسوم", required: false },
    emped_code: { type: "textarea", label: "كود التضمين", required: false },
  },
  chdeliver: {
    challenge: { type: "number", label: "رقم التحدي المرتبط (ID)", required: true },
    figma_link: { type: "url", label: "رابط مشروع Figma", required: true },
    extra_link: { type: "url", label: "رابط إضافي (Figma Jam أو غيره)", required: false },
    status: { type: "hidden", label: "الحالة", required: true, value: "Pending" },
  },
};

/** Map UI type -> WP REST endpoint CPT slug */
const TYPE_SLUGS = {
  posts: "posts", // core posts
  figplg: "figplg",
  figtemp: "figtemp",
  project: "project",
  job: "job",
  jobs: "job", // UI may pass plural
  challenge: "challenge",
  challenges: "challenge", // UI may pass plural
  podcast: "podcast",
  chdeliver: "chdeliver",
  designreview: "designreview"
};

const TYPE_TITLES_AR = {
  posts: "مقالات",
  figplg: "إضافات Figma",
  figtemp: "قوالب Figma",
  project: "مشاريع",
  job: "وظائف",
  challenge: "تحديات",
  podcast: "بودكاست",
  chdeliver: "تسليم تحدي",
  designreview: "مراجعة تصميم",
};

// === Utils ===
const toYmd = (v) => (v ? new Date(v).toISOString().slice(0, 10) : ""); // YYYY-MM-DD
const coerceForAcf = (cfg, v) => {
  if (v == null || v === "") return v;
  switch (cfg.type) {
    case "date":
      return toYmd(v); // ACF Date Picker expects Y-m-d
    case "number":
      return Number(v);
    case "users":
      // ACF User (multiple) expects an array of user IDs
      if (Array.isArray(v)) return v.map((u) => (typeof u === "number" ? u : u?.id)).filter(Boolean);
      return typeof v === "number" ? [v] : [];
    case "select":
    case "text":
    case "textarea":
    case "url":
    case "checkbox":
    default:
      return String(v);
  }
};

// Encode potentially blocked HTML values for safe transport through WP REST
// We prefix with `b64:` so the frontend can detect and decode for rendering
const encodeHtmlForTransport = (raw) => {
  try {
    const encoded = typeof window !== "undefined"
      ? btoa(unescape(encodeURIComponent(String(raw))))
      : Buffer.from(String(raw), "utf8").toString("base64");
    return `b64:${encoded}`;
  } catch (_) {
    return String(raw);
  }
};

function newDraftId() {
  try {
    return crypto.randomUUID();
  } catch {
    return `draft_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }
}

function formatDraftTime(ts) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString("ar-EG");
  } catch {
    return "";
  }
}

function getWpApiBase() {
  const raw = typeof process !== "undefined" ? process.env.NEXT_PUBLIC_BASE_URL || "" : "";
  const base = String(raw).replace(/\/+$/, "");
  return `${base}/api/wp/v2`;
}

function stripHtmlTitle(html = "") {
  return html.replace(/<[^>]+>/g, "").trim();
}

export default function AddNewPost({
  postTypes = ["posts", "figplg", "figtemp", "project", "jobs", "challenges", "podcast", "designreview"],
  initialType = null,
  initialTitle = "",
  initialAcf = {},
  onSuccess = null,
  editPostId = null,
  onCancelEdit = null,
}) {
  const [title, setTitle] = useState(initialTitle || "");
  const [content, setContent] = useState("");
  const [type, setType] = useState(initialType || postTypes[0]);
  const [loading, setLoading] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [hasPermission, setHasPermission] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [featuredImage, setFeaturedImage] = useState(null);
  const [uploadingFeatured, setUploadingFeatured] = useState(false);
  const [acfFields, setAcfFields] = useState({});
  const [currentUser, setCurrentUser] = useState(null);

  const editorRef = useRef(null);
  const articleBodyEditorRef = useRef(null);
  const fileInputRef = useRef(null);
  const featuredImageRef = useRef(null);
  const toolbarImageRef = useRef(null);

  const useRichArticleEditor = (TYPE_SLUGS[type] || type) === "posts";

  // Local drafts (IndexedDB) - posts only
  const [draftId, setDraftId] = useState(() => newDraftId());
  const [drafts, setDrafts] = useState([]);
  const [draftsStatus, setDraftsStatus] = useState({ saving: false, lastSavedAt: null, error: null });
  const [draftWpPostId, setDraftWpPostId] = useState(null);
  const [draftName, setDraftName] = useState("");
  const draftAutosaveTimerRef = useRef(null);
  const localUserId = currentUser?.id ? String(currentUser.id) : "anonymous";

  /** Selected WP status when editing (draft / pending / publish); commadmin & administrator only in UI */
  const [articleStatus, setArticleStatus] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  // Init ACF fields per type
  useEffect(() => {
    const fields = ACF_FIELDS[TYPE_SLUGS[type] || type] || ACF_FIELDS[type] || {};
    const initialValues = {};
    Object.keys(fields).forEach((k) => (initialValues[k] = ""));
    // merge prefilled acf values if provided
    const merged = { ...initialValues };
    if (initialAcf && typeof initialAcf === 'object') {
      Object.keys(initialAcf).forEach((k) => {
        if (k in merged) merged[k] = initialAcf[k];
      });
    }
    setAcfFields(merged);
  }, [type]);

  // Permissions
  useEffect(() => {
    console.log("type", type);
    const check = async () => {
      const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
      if (!token) {
        setHasPermission(false);
        setUserRole(null);
        return;
      }
      try {
        const user = await fetchUser();
        const roles = user?.roles || [];
        const hasAdminRole = roles.includes("administrator") || roles.includes("commadmin");
        const regularAllowed = ["posts", "project", "figplg", "figtemp", "job", "chdeliver", "designreview"]; // pending
        const normalized = TYPE_SLUGS[type] || type;
        const isRegularAllowed = regularAllowed.includes(normalized);
        setUserRole(roles[0] || "subscriber");
        setHasPermission(true);
        setHasPermission(hasAdminRole || isRegularAllowed);
        setCurrentUser(user);
      } catch (e) {
        console.error(e);
        setHasPermission(false);
      }

      console.log("hasPermission", hasPermission);
    };
    check();
  }, [type, hasPermission]);

  useEffect(() => {
    if (!editPostId) {
      setArticleStatus(null);
      return;
    }

    let cancelled = false;

    (async () => {
      setEditLoading(true);
      try {
        const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
        if (!token) {
          toast.error("يجب تسجيل الدخول لتعديل المقال");
          onCancelEdit?.();
          return;
        }

        const user = await fetchUser();
        const apiBase = getWpApiBase();
        const res = await fetch(`${apiBase}/posts/${editPostId}?context=edit`, {
          headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
        });

        if (!res.ok) {
          toast.error("تعذر تحميل المقال للتعديل");
          onCancelEdit?.();
          return;
        }

        const post = await res.json();
        if (cancelled) return;

        if (!canEditArticle(user, post.author)) {
          toast.error("ليس لديك صلاحية تعديل هذا المقال");
          onCancelEdit?.();
          return;
        }

        const rawTitle = post.title?.raw ?? stripHtmlTitle(post.title?.rendered || "");
        const rawContent = typeof post.content?.raw === "string" ? post.content.raw : "";

        setTitle(rawTitle);
        setContent(rawContent);
        const userSelectable = ["draft", "pending", "publish"];
        const nextStatus = userSelectable.includes(post.status) ? post.status : "pending";
        setArticleStatus(nextStatus);
        setDraftWpPostId(editPostId);
        setType("posts");

        const acfSrc = post.acf && typeof post.acf === "object" ? post.acf : {};
        const defs = ACF_FIELDS.posts || {};
        const mergedAcf = {};
        Object.keys(defs).forEach((k) => {
          mergedAcf[k] = "";
          if (acfSrc[k] != null && acfSrc[k] !== "") mergedAcf[k] = acfSrc[k];
        });
        setAcfFields(mergedAcf);

        const fid = post.featured_media;
        if (fid) {
          const mr = await fetch(`${apiBase}/media/${fid}`, {
            headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
          });
          if (mr.ok) {
            const m = await mr.json();
            if (!cancelled) {
              setFeaturedImage({
                id: m.id,
                url: m.source_url,
                title: m.title?.rendered || "",
              });
            }
          }
        } else if (!cancelled) {
          setFeaturedImage(null);
        }

        setShowForm(true);
        setIsSubmitted(false);
      } catch (e) {
        console.error(e);
        toast.error(e?.message || "فشل تحميل المقال");
        onCancelEdit?.();
      } finally {
        if (!cancelled) setEditLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [editPostId, onCancelEdit]);

  const refreshDrafts = async () => {
    if (!useRichArticleEditor) return;
    try {
      const items = await listDrafts({ userId: localUserId, type: "posts" });
      setDrafts(items);
    } catch (e) {
      setDraftsStatus((p) => ({ ...p, error: e?.message || "Failed to load drafts." }));
    }
  };

  useEffect(() => {
    if (!useRichArticleEditor) return;
    refreshDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useRichArticleEditor, localUserId]);

  const persistLocalDraft = async ({ force = false } = {}) => {
    if (!useRichArticleEditor) return;
    if (!hasPermission) return;
    if (!draftId) return;
    const normalizedType = TYPE_SLUGS[type] || type;
    if (normalizedType !== "posts") return;

    // Avoid saving completely empty drafts unless forced.
    const hasAny =
      (title && title.trim()) ||
      (content && content.trim()) ||
      (acfFields && Object.values(acfFields).some((v) => String(v || "").trim())) ||
      featuredImage ||
      (attachments && attachments.length);
    if (!hasAny && !force) return;

    setDraftsStatus((p) => ({ ...p, saving: true, error: null }));
    try {
      const saved = await upsertDraft({
        draftId,
        type: "posts",
        userId: localUserId,
        draftName,
        title,
        content,
        acfFields,
        featuredImage,
        attachments,
        wpPostId: draftWpPostId || null,
      });
      setDraftsStatus({ saving: false, lastSavedAt: saved.updatedAt, error: null });
      await refreshDrafts();
    } catch (e) {
      setDraftsStatus((p) => ({ ...p, saving: false, error: e?.message || "Failed to save local draft." }));
    }
  };

  // Debounced autosave
  useEffect(() => {
    if (!useRichArticleEditor) return;
    if (!hasPermission) return;

    if (draftAutosaveTimerRef.current) {
      clearTimeout(draftAutosaveTimerRef.current);
    }
    draftAutosaveTimerRef.current = setTimeout(() => {
      persistLocalDraft();
    }, 1200);

    return () => {
      if (draftAutosaveTimerRef.current) clearTimeout(draftAutosaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, content, acfFields, featuredImage, attachments, draftId, useRichArticleEditor, hasPermission, localUserId, draftWpPostId]);

  const startNewDraft = async () => {
    setDraftId(newDraftId());
    setDraftWpPostId(null);
    setDraftName("");
    setTitle("");
    setContent("");
    setAttachments([]);
    setFeaturedImage(null);
    setAcfFields((prev) => {
      const defs = ACF_FIELDS["posts"] || {};
      const next = {};
      Object.keys(defs).forEach((k) => (next[k] = ""));
      return next;
    });
    articleBodyEditorRef.current?.clear?.();
    setDraftsStatus({ saving: false, lastSavedAt: null, error: null });
    await refreshDrafts();
  };

  const loadLocalDraft = async (d) => {
    if (!d?.draftId) return;
    setDraftId(d.draftId);
    setDraftWpPostId(d.wpPostId || null);
    setDraftName(d.draftName || "");
    setTitle(d.title || "");
    setContent(d.content || "");
    setAcfFields(d.acfFields || {});
    setFeaturedImage(d.featuredImage || null);
    setAttachments(Array.isArray(d.attachments) ? d.attachments : []);
    setDraftsStatus((p) => ({ ...p, lastSavedAt: d.updatedAt || null, error: null }));
  };

  const removeLocalDraft = async (id) => {
    if (!id) return;
    try {
      await deleteDraft(id);
      toast.success("تم حذف المسودة من المتصفح");
      if (id === draftId) {
        await startNewDraft();
      } else {
        await refreshDrafts();
      }
    } catch (e) {
      toast.error(e?.message || "فشل حذف المسودة");
    }
  };

  const renameLocalDraft = async (d) => {
    if (!d?.draftId) return;
    const nextName = prompt("اسم المسودة", d.draftName || d.title || "مسودة");
    if (nextName === null) return;
    try {
      await upsertDraft({ ...d, draftName: String(nextName || "").trim() });
      if (d.draftId === draftId) setDraftName(String(nextName || "").trim());
      await refreshDrafts();
    } catch (e) {
      toast.error(e?.message || "فشل تحديث اسم المسودة");
    }
  };

  // Rich text toolbar
  const formatText = (cmd, value = null) => {
    document.execCommand(cmd, false, value);
    editorRef.current?.focus();
  };

  const handleShowForm = () => {
    setShowForm(true);
    setIsSubmitted(false);
  };

  // Upload helpers
  const validateFile = (file) => {
    const maxSize = 2 * 1024 * 1024; // 2MB
    const ok = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp", "application/pdf"];
    if (file.size > maxSize) {
      toast.error(`حجم الملف ${file.name} أكبر من 2 ميجابايت`);
      return false;
    }
    if (!ok.includes(file.type)) {
      toast.error(`نوع الملف ${file.name} غير مسموح. الأنواع المسموحة: PDF, JPG, PNG, GIF, WEBP`);
      return false;
    }
    return true;
  };

  const uploadToMedia = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
    const res = await fetch(`${getWpApiBase()}/media`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    if (!res.ok) throw new Error("فشل رفع الملف");
    return res.json();
  };

  const handleFileUpload = async (files) => {
    const valid = Array.from(files).filter(validateFile);
    if (!valid.length) return;
    setUploadingFiles(true);
    try {
      const uploaded = [];
      for (const f of valid) {
        const data = await uploadToMedia(f);
        uploaded.push({ id: data.id, url: data.source_url, title: data.title?.rendered || f.name, type: f.type });
      }
      setAttachments((p) => [...p, ...uploaded]);
      toast.success(`تم رفع ${uploaded.length} ملف بنجاح`);
    } catch (e) {
      toast.error(`فشل رفع الملفات: ${e.message}`);
    } finally {
      setUploadingFiles(false);
    }
  };

  const insertImageToContent = (url) => {
    if (useRichArticleEditor) {
      articleBodyEditorRef.current?.insertImage?.(url);
      return;
    }
    const imgTag = `<img src="${url}" alt="صورة" style="max-width: 100%; height: auto;" />`;
    document.execCommand("insertHTML", false, imgTag);
    editorRef.current?.focus();
  };

  const handleFeaturedImageUpload = async (files) => {
    const file = files?.[0];
    if (!file) return;
    if (!validateFile(file)) return;
    if (!file.type.startsWith("image/")) return toast.error("يجب أن يكون الملف صورة");
    setUploadingFeatured(true);
    try {
      const data = await uploadToMedia(file);
      setFeaturedImage({ id: data.id, url: data.source_url, title: data.title?.rendered || file.name });
      toast.success("تم رفع الصورة بنجاح");
    } catch (e) {
      toast.error(`فشل رفع الصورة المميزة: ${e.message}`);
    } finally {
      setUploadingFeatured(false);
    }
  };

  const handleToolbarImageUpload = async (files) => {
    const file = files?.[0];
    if (!file) return;
    if (!validateFile(file)) return;
    if (!file.type.startsWith("image/")) return toast.error("يجب أن يكون الملف صورة");
    try {
      const data = await uploadToMedia(file);
      const url = data.source_url;
      if (useRichArticleEditor) {
        articleBodyEditorRef.current?.insertImage?.(url);
      } else {
        const imgTag = `<img src="${url}" alt="${data.title?.rendered || file.name}" style="max-width: 100%; height: auto;" />`;
        document.execCommand("insertHTML", false, imgTag);
        editorRef.current?.focus();
      }
      setAttachments((p) => [...p, { id: data.id, url, title: data.title?.rendered || file.name, type: file.type }]);
      toast.success("تم إدراج الصورة في المحتوى بنجاح");
    } catch (e) {
      toast.error(`فشل رفع الصورة: ${e.message}`);
    }
  };

  // Build payload using ACF object (requires ACF field group "Show in REST API")
  const buildPayloadWithACF = ({ normalizedType, title, content, isAdmin, featuredImage, acfFields, existingStatus, explicitStatus }) => {
    const defs = ACF_FIELDS[normalizedType] || {};
    const acf = {};
    for (const key of Object.keys(acfFields || {})) {
      const cfg = defs[key];
      if (!cfg) continue;
      let val = cfg.type === 'hidden' ? cfg.value : coerceForAcf(cfg, acfFields[key]);
      // Special handling for embed HTML field to avoid server-side sanitization stripping it

      // if (key === "emped_code" && typeof val === "string" && val.trim()) {
      //   val = encodeHtmlForTransport(val);
      // }

      if (val !== undefined && val !== "") acf[key] = val;
    }
    const allowedExplicit = ["draft", "pending", "publish"];
    const status =
      explicitStatus && allowedExplicit.includes(explicitStatus)
        ? explicitStatus
        : existingStatus != null && existingStatus !== ""
          ? existingStatus
          : isAdmin
            ? "publish"
            : "pending";
    const payload = {
      title,
      content,
      status,
      featured_media: featuredImage?.id || 0,
    };
    if (Object.keys(acf).length) payload.acf = acf; // << key point
    return payload;
  };

  const notifySubmissionByEmail = async ({ postId, normalizedType }) => {
    try {
      const baseURL = process.env.NEXT_PUBLIC_BASE_URL;
      const name = currentUser?.name || currentUser?.display_name || currentUser?.user_nicename || '';
      const email = currentUser?.email || currentUser?.user_email || '';
      const challengeId = acfFields?.challenge || '';
      const message = `تم إرسال تسليم جديد للتحدي #${challengeId}.\n\nالعنوان: ${title}\nالنوع: ${normalizedType}\nرابط التسليم: ${baseURL}wp-admin/post.php?post=${postId}&action=edit`;
      await fetch(`${baseURL}api/ticket/v1/ticket`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          name: name || 'User',
          email: email || 'noreply@figmawya.com',
          type: 'Challenge Delivery',
          message,
        }),
      }).catch(() => { });
    } catch (_) {
      // ignore notification failures
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
    if (!token) {
      toast.error("يجب تسجيل الدخول أولاً");
      setLoading(false);
      return;
    }
    if (!hasPermission) {
      toast.error("ليس لديك صلاحية لإضافة هذا النوع من المحتوى.");
      setLoading(false);
      return;
    }

    // validate required ACF fields
    const normalizedType = TYPE_SLUGS[type] || type;
    const defs = ACF_FIELDS[normalizedType] || {};
    const required = Object.keys(defs).filter((k) => defs[k].required);
    const missing = required.filter((k) => !acfFields[k] || `${acfFields[k]}`.trim() === "");
    if (missing.length) {
      const labels = missing.map((k) => defs[k].label).join(", ");
      toast.error(`يرجى ملء الحقول المطلوبة: ${labels}`);
      setLoading(false);
      return;
    }

    try {
      const roles = currentUser?.roles || [];
      const isAdmin = roles.includes("administrator") || roles.includes("commadmin");
      const apiBase = getWpApiBase();
      const isEditPosts =
        Boolean(editPostId) && normalizedType === "posts";
      const payload = buildPayloadWithACF({
        normalizedType,
        title,
        content,
        isAdmin,
        featuredImage,
        acfFields,
        existingStatus: null,
        explicitStatus: isEditPosts ? articleStatus : null,
      });

      const endpoint = isEditPosts
        ? `${apiBase}/posts/${editPostId}`
        : `${apiBase}/${normalizedType}`;

      const res = await fetch(endpoint, {
        method: isEditPosts ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });

      if (res.status === 401 || res.status === 403) {
        toast.error(
          isEditPosts
            ? "ليس لديك صلاحية لتعديل هذا المحتوى."
            : "ليس لديك صلاحية لإضافة هذا النوع من المحتوى."
        );
        setLoading(false);
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.message || (isEditPosts ? "حدث خطأ أثناء حفظ التعديلات" : "حدث خطأ أثناء الإضافة"));
      }

      const post = await res.json();
      const postId = post?.id;

      // Link attachments
      if (attachments.length && postId) {
        try {
          for (const att of attachments) {
            await fetch(`${apiBase}/media/${att.id}`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify({ post: postId }),
            });
          }
        } catch (err) {
          console.warn("Failed to link attachments:", err);
        }
      }

      toast.success(
        isEditPosts
          ? "تم حفظ التعديلات بنجاح"
          : isAdmin
            ? "تمت إضافة المحتوى بنجاح"
            : "تم إرسال المحتوى للمراجعة"
      );

      // fire-and-forget notification email for challenge delivery
      if ((TYPE_SLUGS[type] || type) === 'chdeliver' && postId) {
        notifySubmissionByEmail({ postId, normalizedType });
      }

      // reset (skip full reset when editing — parent may close edit mode)
      if (!isEditPosts) {
        setTitle("");
        setContent("");
        setAttachments([]);
        setFeaturedImage(null);
        setAcfFields({});
        if (useRichArticleEditor) {
          articleBodyEditorRef.current?.clear?.();
          setDraftWpPostId(null);
          setDraftId(newDraftId());
        } else if (editorRef.current) {
          editorRef.current.innerHTML = "";
        }
        setShowForm(false);
        setIsSubmitted(true);
      } else {
        setArticleStatus(post?.status && ["draft", "pending", "publish"].includes(post.status) ? post.status : "draft");
      }
      onSuccess && onSuccess({ id: postId, type: TYPE_SLUGS[type] || type });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendToWpDraft = async () => {
    try {
      if (!hasPermission) return toast.error("ليس لديك صلاحية.");
      const normalizedType = TYPE_SLUGS[type] || type;
      if (normalizedType !== "posts") return toast.error("حفظ المسودة متاح للمقالات فقط.");
      const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
      if (!token) return toast.error("لابد ان تسجل الدخول");

      setLoading(true);
      const apiBase = getWpApiBase();
      const payload = buildPayloadWithACF({
        normalizedType,
        title,
        content,
        isAdmin: true, // irrelevant for drafts
        featuredImage,
        acfFields,
        existingStatus: articleStatus,
      });
      payload.status = "draft";

      const endpoint = draftWpPostId
        ? `${apiBase}/posts/${draftWpPostId}`
        : `${apiBase}/posts`;

      const res = await fetch(endpoint, {
        method: draftWpPostId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.message || "فشل حفظ المسودة في فيجماوية");
      }
      const post = await res.json();
      const wpId = post?.id;
      if (wpId) {
        setDraftWpPostId(wpId);
        await upsertDraft({
          draftId,
          type: "posts",
          userId: localUserId,
          draftName,
          title,
          content,
          acfFields,
          featuredImage,
          attachments,
          wpPostId: wpId,
        });
        await refreshDrafts();
      }
      toast.success("تم حفظ المسودة في فيجماوية");
      setArticleStatus("draft");
    } catch (e) {
      toast.error(e?.message || "فشل حفظ المسودة");
    } finally {
      setLoading(false);
    }
  };

  // UI helpers
  const renderAcfField = (fieldKey, cfg) => {
    const value = acfFields[fieldKey] || "";
    if (cfg.type === 'hidden') {
      return (
        <input type="hidden" value={cfg.value || ''} />
      );
    }
    switch (cfg.type) {
      case "textarea":
        return (
          <textarea className="form-control" value={value} onChange={(e) => setAcfFields((p) => ({ ...p, [fieldKey]: e.target.value }))} required={cfg.required} rows={3} />
        );
      case "select":
        return (
          <select className="form-select" value={value} onChange={(e) => setAcfFields((p) => ({ ...p, [fieldKey]: e.target.value }))} required={cfg.required}>
            <option value="">اختر...</option>
            {cfg.options.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        );
      case "url":
        return (
          <input type="url" className="form-control" value={value} onChange={(e) => setAcfFields((p) => ({ ...p, [fieldKey]: e.target.value }))} required={cfg.required} placeholder="https://example.com" />
        );
      case "number":
        return (
          <input type="number" className="form-control" value={value} onChange={(e) => setAcfFields((p) => ({ ...p, [fieldKey]: e.target.value }))} required={cfg.required} />
        );
      case "date":
        return (
          <div>
            <input type="date" className="form-control" value={value} onChange={(e) => setAcfFields((p) => ({ ...p, [fieldKey]: e.target.value }))} required={cfg.required} />
            <small className="text-muted">سيتم إرسال التاريخ بالتنسيق: YYYY-MM-DD</small>
          </div>
        );
      case "file":
        return (
          <div>
            <input type="file" className="form-control" accept={cfg.accept} onChange={(e) => setAcfFields((p) => ({ ...p, [fieldKey]: e.target.files?.[0] }))} required={cfg.required} />
            {acfFields[fieldKey] && <div className="mt-2"><small className="text-success">تم اختيار الملف: {acfFields[fieldKey].name}</small></div>}
          </div>
        );
      case "users":
        return (
          <div>
            <div className="alert alert-info">حقل المشاركين سيتم إدارته تلقائياً عند انضمام المستخدمين للتحدي</div>
            <input type="text" className="form-control" value="سيتم إدارته تلقائياً" disabled readOnly />
          </div>
        );
      case "hidden":
        return (
          <input type="hidden" className="form-control" value={value} onChange={(e) => setAcfFields((p) => ({ ...p, [fieldKey]: e.target.value }))} />
        );
      default:
        return (
          <input type="text" className="form-control" value={value} onChange={(e) => setAcfFields((p) => ({ ...p, [fieldKey]: e.target.value }))} required={cfg.required} />
        );
    }
  };

  // Early no-permission view
  if (!hasPermission && userRole !== null) {
    const isRegularUser = !["commadmin", "administrator"].includes(userRole);
    const restricted = ["challenge"]; // adjust as needed
    const normalized = TYPE_SLUGS[type] || type;
    const isRestrictedType = restricted.includes(normalized);
    return (
      <div className="card p-4 my-4">
        <div className="alert alert-warning">
          <h5>لا توجد صلاحية</h5>
          <p>عذراً، لا يمكنك إضافة محتوى من نوع "{normalized}".</p>
          {isRegularUser && isRestrictedType ? (
            <p>مطلوب دور <strong>مشرف مجتمع فيجماوية</strong> أو <strong>مدير مجتمع فيجماوية</strong> لإضافة هذا النوع من المحتوى.</p>
          ) : isRegularUser ? (
            <p>
              يمكن لأعضاء مجتمع فيجماوية إضافة محتوى من نوع <strong>project</strong> و <strong>figplg</strong> و <strong>figtemp</strong> و <strong>posts</strong> و <strong>job</strong> (بانتظار الموافقة).
            </p>
          ) : (
            <p>لا توجد صلاحية لإضافة هذا النوع من المحتوى.</p>
          )}
          <p>دورك الحالي: <strong>{userRole === "commadmin" ? "مشرف مجتمع فيجماوية" : userRole === "administrator" ? "مدير مجتمع فيجماوية" : "عضو مجتمع فيجماوية"}</strong></p>
        </div>
      </div>
    );
  }

  // Success view
  if (!showForm && isSubmitted) {
    return (
      <div className="card p-4 my-4">
        <div className="text-center">
          <div className="alert alert-success">
            <i className="bi bi-check-circle-fill fs-1 text-success"></i>
            <h4 className="mt-3">تم إرسال المحتوى بنجاح</h4>
            <p className="text-muted">سيتم مراجعة المحتوى والموافقة عليه من قبل الإدارة.</p>
          </div>
          <button onClick={handleShowForm} className="btn btn-primary">
            <i className="bi bi-plus-lg"></i> إضافة محتوى جديد
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="card p-4 my-4">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h2 className="mb-0">{editPostId ? "تعديل المقال" : "إضافة محتوى جديد"}</h2>
        {editPostId ? (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => onCancelEdit?.()}
            disabled={loading || editLoading}
          >
            إلغاء التحرير
          </button>
        ) : null}
      </div>

      {editLoading && editPostId ? (
        <div className="alert alert-light border mb-3">
          <span className="spinner-border spinner-border-sm me-2" role="status" />
          جاري تحميل بيانات المقال…
        </div>
      ) : null}

      {userRole && (
        <div className="alert alert-info mb-3">
          <small>
            <i className="bi bi-shield-check"></i> دورك: <strong>{userRole === "commadmin" ? "مشرف مجتمع فيجماوية" : userRole === "administrator" ? "مدير مجتمع فيجماوية" : "عضو مجتمع فيجماوية"}</strong> {hasPermission && " - لديك صلاحية الإضافة"}
          </small>
        </div>
      )}

      {/* Keep type selector hidden for now */}
      <div className="mb-3 d-none">
        <label className="form-label">نوع المحتوى</label>
        <select className="form-select" value={type} onChange={(e) => setType(e.target.value)}>
          {postTypes.map((pt) => (
            <option key={pt} value={pt}>{TYPE_TITLES_AR[TYPE_SLUGS[pt] || pt] || pt}</option>
          ))}
        </select>
        <small className="text-muted">اختر نوع المحتوى لعرض الحقول المخصصة المناسبة</small>
      </div>

      <div className="mb-3">
        <label className="form-label">العنوان</label>
        <input
          className="form-control"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          disabled={Boolean(editPostId && editLoading)}
        />
      </div>

      {editPostId &&
      !editLoading &&
      (currentUser?.roles?.includes("commadmin") || currentUser?.roles?.includes("administrator")) ? (
        <div className="mb-3">
          <label className="form-label">حالة المقال</label>
          <select
            className="form-select"
            value={articleStatus || "pending"}
            onChange={(e) => setArticleStatus(e.target.value)}
            disabled={loading}
          >
            <option value="draft">مسودة</option>
            <option value="pending">بانتظار المراجعة</option>
            <option value="publish">منشور</option>
          </select>
          <small className="text-muted">مسودة، أو إرسال للمراجعة، أو نشر (للمشرفين ومديري المجتمع عند تعديل المقال)</small>
        </div>
      ) : null}

      {/* Featured image */}
      {type !== 'designreview' && (
        <div className="mb-3">
          <label className="form-label">الغلاف Cover</label>
          <div className="border rounded p-3">
            {!featuredImage ? (
              <div>
                <input type="file" accept=".jpg,.jpeg,.png,.gif,.webp" onChange={(e) => handleFeaturedImageUpload(e.target.files)} className="form-control" disabled={uploadingFeatured} />
                <small className="text-muted">الحد الأقصى: 2 ميجابايت. الأنواع المسموحة: JPG, PNG, GIF, WEBP</small>
                {uploadingFeatured && <div className="alert alert-info mt-2"><i className="bi bi-arrow-clockwise spin"></i> جاري رفع الصورة المميزة...</div>}
              </div>
            ) : (
              <div className="text-center">
                <img src={featuredImage.url} alt={featuredImage.title} className="img-fluid mb-2" style={{ maxHeight: "200px", objectFit: "cover" }} />
                <div className="d-flex justify-content-center gap-2">
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => featuredImageRef.current?.click()}><i className="bi bi-arrow-repeat"></i> تغيير</button>
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setFeaturedImage(null)}><i className="bi bi-trash"></i> حذف</button>
                </div>
                <input ref={featuredImageRef} type="file" accept=".jpg,.jpeg,.png,.gif,.webp" onChange={(e) => handleFeaturedImageUpload(e.target.files)} className="d-none" disabled={uploadingFeatured} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="mb-3">
        <label className="form-label">المحتوى</label>
        {useRichArticleEditor ? (
          <>
            <div className="border rounded p-3 mb-2 bg-light">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div>
                  <div className="fw-bold">مسودات المتصفح</div>
                  <div className="small text-muted">
                    {draftsStatus.saving
                      ? "جاري الحفظ..."
                      : draftsStatus.lastSavedAt
                        ? `آخر حفظ على جهازي: ${formatDraftTime(draftsStatus.lastSavedAt)}`
                        : "ابدأ الكتابة وسيتم الحفظ تلقائياً"}
                  </div>
                  {draftsStatus.error ? <div className="small text-danger">{draftsStatus.error}</div> : null}
                </div>
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => persistLocalDraft({ force: true })} disabled={loading}>
                    حفظ على جهازي
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-dark" onClick={startNewDraft} disabled={loading}>
                    مسودة جديدة
                  </button>
                  <button type="button" className="btn btn-sm btn-primary" onClick={handleSendToWpDraft} disabled={loading}>
                    حفظ كمسودة في فيجماوية
                  </button>
                </div>
              </div>

              <div className="mt-2">
                <label className="form-label small mb-1">اسم المسودة</label>
                <input
                  className="form-control form-control-sm"
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  placeholder="مثال: مقال عن Typography"
                  disabled={loading}
                />
              </div>

              {drafts.length ? (
                <div className="mt-3">
                  <div className="small fw-bold mb-2">كل المسودات</div>
                  <div className="d-flex flex-column gap-2">
                    {drafts.map((d) => {
                      const label =
                        d.draftName?.trim()
                          ? d.draftName.trim()
                          : d.title?.trim()
                            ? d.title.trim()
                            : "مسودة بدون عنوان";
                      return (
                        <div key={d.draftId} className="d-flex justify-content-between align-items-center border rounded p-2 bg-white">
                          <div className="me-2">
                            <div className="fw-bold small">{label}</div>
                            <div className="text-muted small">
                              {formatDraftTime(d.updatedAt)} {d.wpPostId ? `· WP#${d.wpPostId}` : ""}
                            </div>
                          </div>
                          <div className="d-flex gap-2">
                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => loadLocalDraft(d)} disabled={loading}>
                              تحميل
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => renameLocalDraft(d)} disabled={loading}>
                              تسمية
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeLocalDraft(d.draftId)} disabled={loading}>
                              حذف
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="small text-muted mt-2">لا توجد مسودات محفوظة بعد.</div>
              )}
            </div>

            <ArticleBodyEditor
              ref={articleBodyEditorRef}
              value={content}
              onChange={setContent}
              disabled={loading || Boolean(editPostId && editLoading)}
              onUploadImage={async (file) => {
                // reuse existing media upload
                const data = await uploadToMedia(file);
                const url = data.source_url;
                setAttachments((p) => [...p, { id: data.id, url, title: data.title?.rendered || file.name, type: file.type }]);
                return url;
              }}
            />
          </>
        ) : (
          <div className="border rounded">
            <div className="p-2 border-bottom bg-light">
              <button type="button" className="btn btn-sm btn-outline-secondary me-1" onClick={() => formatText("bold")} title="عريض"><i className="bi bi-type-bold"></i></button>
              <button type="button" className="btn btn-sm btn-outline-secondary me-1" onClick={() => formatText("italic")} title="مائل"><i className="bi bi-type-italic"></i></button>
              <button type="button" className="btn btn-sm btn-outline-secondary me-1" onClick={() => formatText("underline")} title="تحت خط"><i className="bi bi-type-underline"></i></button>
              <button type="button" className="btn btn-sm btn-outline-secondary me-1" onClick={() => formatText("insertUnorderedList")} title="قائمة نقطية"><i className="bi bi-list-ul"></i></button>
              <button type="button" className="btn btn-sm btn-outline-secondary me-1" onClick={() => formatText("insertOrderedList")} title="قائمة مرقمة"><i className="bi bi-list-ol"></i></button>
              <button type="button" className="btn btn-sm btn-outline-secondary me-1" onClick={() => { const url = prompt("أدخل الرابط:"); if (url) formatText("createLink", url); }} title="إضافة رابط"><i className="bi bi-link-45deg"></i></button>
              <button type="button" className="btn btn-sm btn-outline-secondary me-1" onClick={() => toolbarImageRef.current?.click()} title="رفع وإدراج صورة"><i className="bi bi-image"></i></button>
              <input ref={toolbarImageRef} type="file" accept=".jpg,.jpeg,.png,.gif,.webp" onChange={(e) => handleToolbarImageUpload(e.target.files)} className="d-none" />
            </div>
            <div ref={editorRef} className="p-3" contentEditable style={{ minHeight: "200px", outline: "none", fontFamily: "inherit", fontSize: "inherit" }} onInput={(e) => setContent(e.currentTarget.innerHTML)} suppressContentEditableWarning />
          </div>
        )}
      </div>

      {/* ACF Fields */}
      {Object.keys(ACF_FIELDS[TYPE_SLUGS[type] || type] || {}).length > 0 && (
        <div className="mb-4">
          <div className="card border-primary">
            {/* <div className="card-header bg-light">
              <h5 className="mb-0"><i className="bi bi-gear-fill me-2"></i> معلومات إضافية - {(TYPE_TITLES_AR[TYPE_SLUGS[type] || type]) || type}</h5>
            </div> */}
            <div className="card-body">
              {/* <div className="alert alert-info mb-3">
                <i className="bi bi-info-circle me-2"></i>
                هذا النوع من المحتوى يحتوي على <strong>{Object.keys(ACF_FIELDS[TYPE_SLUGS[type] || type]).length}</strong> معلومات إضافية.
                {Object.keys(ACF_FIELDS[TYPE_SLUGS[type] || type]).filter((k) => ACF_FIELDS[TYPE_SLUGS[type] || type][k].required).length > 0 && (
                  <span className="text-danger"> الحقول المطلوبة محددة بعلامة *</span>
                )}
              </div> */}
              {Object.keys(ACF_FIELDS[TYPE_SLUGS[type] || type] || {}).map((fieldKey) => (
                <div key={fieldKey} className="mb-3">
                  <label className="form-label fw-bold">
                    {ACF_FIELDS[TYPE_SLUGS[type] || type][fieldKey].label}
                    {ACF_FIELDS[TYPE_SLUGS[type] || type][fieldKey].required && <span className="text-danger"> *</span>}
                  </label>
                  {renderAcfField(fieldKey, ACF_FIELDS[TYPE_SLUGS[type] || type][fieldKey])}
                  {ACF_FIELDS[TYPE_SLUGS[type] || type][fieldKey].required && <small className="text-muted">هذا الحقل مطلوب</small>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Attachments */}
      {type !== 'designreview' && (
        <div className="mb-3">
          <label className="form-label">المرفقات</label>
          <div className="border rounded p-3">
            <div className="mb-3">
              <input ref={fileInputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.gif,.webp" onChange={(e) => handleFileUpload(e.target.files)} className="form-control" disabled={uploadingFiles} />
              <small className="text-muted">الحد الأقصى: 2 ميجابايت لكل ملف. الأنواع المسموحة: PDF, JPG, PNG, GIF, WEBP</small>
            </div>
            {uploadingFiles && <div className="alert alert-info"><i className="bi bi-arrow-clockwise spin"></i> جاري رفع الملفات...</div>}
            {attachments.length > 0 && (
              <div className="mt-3">
                <h6>الملفات المرفقة:</h6>
                <div className="row">
                  {attachments.map((file, index) => (
                    <div key={index} className="col-md-6 col-lg-4 mb-2">
                      <div className="card">
                        <div className="card-body p-2">
                          {file.type?.startsWith("image/") ? (
                            <img src={file.url} alt={file.title} className="img-fluid mb-2" style={{ maxHeight: "100px", objectFit: "cover" }} />
                          ) : (
                            <div className="text-center mb-2"><i className="bi bi-file-pdf fs-1 text-danger"></i></div>
                          )}
                          <div className="d-flex justify-content-between align-items-center">
                            <small className="text-truncate">{file.title}</small>
                            <div>
                              {file.type?.startsWith("image/") && (
                                <button type="button" className="btn btn-sm btn-outline-primary me-1" onClick={() => insertImageToContent(file.url)} title="إدراج في المحتوى"><i className="bi bi-image"></i></button>
                              )}
                              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setAttachments((p) => p.filter((_, i) => i !== index))} title="حذف"><i className="bi bi-trash"></i></button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* has permission: {hasPermission} */}

      <button
        className="btn btn-primary"
        type="submit"
        disabled={loading || !hasPermission || editLoading}
      >
        {loading
          ? editPostId
            ? "جارٍ الحفظ..."
            : "جارٍ الإضافة..."
          : editPostId
            ? "حفظ التعديلات"
            : "إضافة"}
      </button>
    </form>
  );
}
